/**
 * Melio Therapeutics service catalogue — genetics-first longevity offering.
 * Used for display, booking, and Stripe product mapping.
 */

export type ServiceCategory = 'assessment' | 'subscription' | 'consult' | 'add_on';

export interface ServiceProduct {
  id: string;
  name: string;
  priceGbp: number;
  priceNote?: string;
  category: ServiceCategory;
  description: string;
  stripePriceId?: string;
  /** For subscriptions: billing interval */
  interval?: 'month' | 'year';
  /** What's included (bullet points for UI) */
  includes: string[];
}

export const SERVICE_CATALOGUE: ServiceProduct[] = [
  {
    id: 'assessment_only',
    name: 'Intro assessment',
    priceGbp: 550,
    category: 'assessment',
    description: 'Clinical assessment with structured health and family-history intake, genetics-led review, and a clinician-reviewed testing schedule.',
    includes: [
      'Structured health and family-history intake',
      'Genetics and family-history led review',
      'Clinician-reviewed testing schedule and prevention plan',
      'Physician breakdown of your results',
      'Explained next steps and monitoring logic',
    ],
  },
  {
    // The `core_membership` id is retained deliberately. It is the key for the
    // live Stripe price env vars (STRIPE_PRICE_CORE_MEMBERSHIP_MONTHLY), it is
    // stamped on every historical `service_payments` record, and existing
    // checkout links carry `?package=core_membership`. Only the display name
    // changed when Plus was retired — renaming the id would require a Stripe
    // change plus a Firestore migration.
    id: 'core_membership',
    name: 'Melio membership',
    priceGbp: 240,
    priceNote: 'per month, 12-month commitment',
    category: 'subscription',
    description: '12-month structured prevention membership billed monthly, with quarterly review rhythm, clinician-reviewed plan updates, and longitudinal monitoring continuity.',
    interval: 'month',
    includes: [
      'Living prevention dashboard with due-next guidance',
      'Quarterly clinician-reviewed plan refreshes',
      'Structured follow-up, reminders, and open-loop tracking',
      'Secure record for uploads, results, and family context',
      'Care navigation between consultations',
      'Billed monthly on a 12-month commitment',
      'Add-ons billed separately',
    ],
  },
  {
    id: 'one_off_consult',
    name: 'One-off doctor consult',
    priceGbp: 85,
    category: 'consult',
    description: 'Focused physician follow-up for a specific question, result set, or treatment decision.',
    includes: [
      'Focused physician follow-up',
      'Review a question, result set, or treatment decision',
      'Best for existing members or targeted advice',
    ],
  },
  {
    id: 'biological_age_add_on',
    name: 'Biological age add-on',
    priceGbp: 295,
    category: 'add_on',
    description: 'DunedinPACE biological age testing via TruDiagnostic, integrated into your Melio record.',
    includes: [
      'DunedinPACE biological age output',
      'TruDiagnostic-managed testing workflow',
      'Integrated into your Melio health record',
      'Priced separately from membership',
    ],
  },
];

export const getServiceById = (id: string) =>
  SERVICE_CATALOGUE.find((s) => s.id === id);

export const getSubscriptionProduct = () =>
  SERVICE_CATALOGUE.find((s) => s.category === 'subscription');
